/** Deterministic synthetic content for spikes and performance tests. */
import { counterIds, type NodeId } from './ids.ts'
import { makeNode, type Node } from './model.ts'
import { keysBetween } from './order.ts'
import { int, mulberry32, pick, type Rand } from './rng.ts'

const WORDS = (
  'the a to and of in for with on we they client call meeting notes follow up proposal budget ' +
  'timeline scope pilot data tracking dashboard funnel conversion checkout mobile app web ' +
  'session replay heatmap segment analysis insight team partner agency deal contract invoice ' +
  'week month quarter review plan next step owner deadline risk issue fix release test ' +
  'Angebot Termin Kunde Projekt Rechnung Besprechung nächste Woche bitte prüfen erledigt ' +
  'Entwurf Freigabe Abstimmung Ziel Ergebnis offen geklärt Frage Antwort ' +
  'Singapore Bali Jakarta Sydney Berlin Frankfurt consent tag event metric baseline target ' +
  'experiment hypothesis variant uplift revenue onboarding activation retention churn'
).split(' ')

const PAGES = [
  'Dyson',
  'Contentsquare',
  'Up Reply',
  'PMI',
  'Drumline Digital',
  'PRUV',
  'Evan Castle',
  'Rishi Bahri',
  'Google Analytics',
  'Phase 1',
]
const TAGS = ['meeting', 'task', 'dyson', 'contentsquare', 'poc', 'singapore', 'idea']

function sentence(rand: Rand): string {
  const n = int(rand, 3, 22)
  const w: string[] = []
  for (let i = 0; i < n; i++) w.push(pick(rand, WORDS))
  const r = rand()
  if (r < 0.08) w.splice(int(rand, 0, w.length), 0, `[[${pick(rand, PAGES)}]]`)
  else if (r < 0.13) w.push(`#${pick(rand, TAGS)}`)
  else if (r < 0.16) w.splice(int(rand, 0, w.length - 1), 1, `**${pick(rand, WORDS)}**`)
  else if (r < 0.18) w.push(`\`${pick(rand, WORDS)}()\``)
  const s = w.join(' ')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

export function blockText(rand: Rand): string {
  const r = rand()
  if (r < 0.05) {
    const parts: string[] = []
    for (let i = int(rand, 2, 5); i > 0; i--) parts.push(sentence(rand) + '.')
    return parts.join(' ')
  }
  if (r < 0.08) {
    const lines: string[] = []
    for (let i = int(rand, 2, 3); i > 0; i--) lines.push(sentence(rand))
    return lines.join('\n')
  }
  if (r < 0.11) return `TODO ${sentence(rand)}`
  return sentence(rand)
}

interface Draft {
  id: NodeId
  content: string
  collapsed: boolean
  children: Draft[]
}

export interface GeneratedPage {
  readonly nodes: Node[]
  readonly pageId: NodeId
  /** Root of the branch with exactly `bigDescendants` descendants. */
  readonly bigId: NodeId
  readonly blockCount: number
}

/**
 * A page with `blocks` blocks: a random outline (depth ≤ 7, a few collapsed branches) with one
 * top-level branch of exactly `bigDescendants` descendants placed in the middle.
 */
export function generatePage(opts: {
  blocks: number
  seed: number
  bigDescendants?: number
  pageId?: NodeId
  idPrefix?: string
  now?: number
}): GeneratedPage {
  const rand = mulberry32(opts.seed)
  const newId = counterIds(opts.idPrefix ?? 'b')
  const now = opts.now ?? 0
  const pageId = opts.pageId ?? 'page'
  const bigN = opts.bigDescendants ?? 500
  const mk = (content: string): Draft => ({ id: newId(), content, collapsed: false, children: [] })

  // The big branch: c children, the rest spread as grandchildren.
  const big = mk(`Big branch with ${bigN} descendants`)
  const c = Math.max(1, Math.round(Math.sqrt(bigN)))
  for (let i = 0; i < c; i++) big.children.push(mk(blockText(rand)))
  for (let i = 0; i < bigN - c; i++) big.children[i % c]!.children.push(mk(blockText(rand)))

  const regular = opts.blocks - (bigN + 1)
  const roots: Draft[] = []
  const path: Draft[] = []
  let made = 0
  const insertAt = Math.max(0, Math.floor(regular / 2))
  let bigPlaced = false
  while (made < regular) {
    if (made === insertAt && !bigPlaced) {
      roots.push(big)
      bigPlaced = true
      path.length = 0
    }
    const d = mk(blockText(rand))
    const r = rand()
    let depth = path.length
    if (r < 0.28 && depth < 7 && path.length > 0) depth = path.length
    else if (r < 0.75) depth = Math.max(0, path.length - 1)
    else depth = Math.max(0, path.length - 1 - int(rand, 1, 3))
    if (path.length === 0) depth = 0
    path.length = depth
    if (depth === 0) roots.push(d)
    else path[depth - 1]!.children.push(d)
    path.push(d)
    made++
  }
  if (!bigPlaced) roots.push(big)

  const nodes: Node[] = [makeNode({ id: pageId, kind: 'page', title: `Spike page ${pageId}`, createdAt: now, updatedAt: now })]
  const emit = (list: Draft[], parentId: NodeId): void => {
    const keys = keysBetween(null, null, list.length, rand)
    list.forEach((d, i) => {
      const collapsed = d !== big && d.children.length > 0 && rand() < 0.03
      nodes.push(
        makeNode({
          id: d.id,
          kind: 'block',
          content: d.content,
          parentId,
          order: keys[i]!,
          collapsed,
          createdAt: now,
          updatedAt: now,
        }),
      )
      emit(d.children, d.id)
    })
  }
  emit(roots, pageId)
  return { nodes, pageId, bigId: big.id, blockCount: nodes.length - 1 }
}
